/**
 * HARD MODE Context
 * 
 * Founder Feature (killer):
 * - No dashboard
 * - No pipeline
 * - Only: One Thing, Mandatory, Call/WhatsApp
 * 
 * Slogan: "Se vuoi risultati diversi, lavora in modo diverso."
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface HardModeContextType {
  isHardMode: boolean;
  toggleHardMode: () => void;
}

const HardModeContext = createContext<HardModeContextType | undefined>(undefined);

export function HardModeProvider({ children }: { children: ReactNode }) {
  const [isHardMode, setIsHardMode] = useState(() => {
    // Check localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hardMode') === 'true';
    }
    return false;
  });
  const location = useLocation();
  const navigate = useNavigate();

  // Save to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hardMode', isHardMode.toString());
    }
  }, [isHardMode]);

  // Redirect if in hard mode and trying to access blocked pages
  useEffect(() => {
    if (isHardMode) {
      const blockedPaths = [
        '/app/dashboard',
        '/app/pipeline',
        '/app/insights',
        '/app/leads',
        '/app/calendar',
        '/app/learning',
      ];
      
      if (blockedPaths.includes(location.pathname)) {
        navigate('/app/today', { replace: true });
      }
    }
  }, [isHardMode, location.pathname, navigate]);

  const toggleHardMode = () => {
    setIsHardMode(prev => !prev);
  };

  return (
    <HardModeContext.Provider value={{ isHardMode, toggleHardMode }}>
      {children}
    </HardModeContext.Provider>
  );
}

export function useHardMode() {
  const context = useContext(HardModeContext);
  if (!context) {
    throw new Error('useHardMode must be used within HardModeProvider');
  }
  return context;
}

